import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { AuthType } from 'constants/constants'

import { Box, Button, Grid, Modal, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay, SearchBox } from 'components/commonparts'
import {
  collectRowNodesFromGridSelection,
  getClipboardContextMenuItems,
  useGridEdit,
} from 'hooks/useGridEdit'
import { useGridSearch } from 'hooks/useGridSearch'
import { useItem } from 'hooks/useItems'
import { DialogWrapper } from 'templates'
import { initRecordEditEx, type RecordEditEx } from 'types/dataGridTypes'

import type { DefaultMenuItem, GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-community'
import type { ColDef } from 'ag-grid-enterprise'
import type { AlertMstItem, GetMstItemsItemGetRequest, MstItem, ResponseResult } from 'api-client'

export type ItemAlertDialogEdit = {
  orgItemCode: string
  masterRegister?: boolean
  isDiscontinued?: boolean
  isMuted?: boolean
}

type ItemAlertDialogProps = {
  open: boolean
  onClose: () => void
  onApplyEdits: (edits: ItemAlertDialogEdit[]) => void
  onNavigateToRecord: (target: { orgItemCode: string }) => void
  resetSignal: number
  searchParam: GetMstItemsItemGetRequest
  // center: MstCenter[]
  items: MstItem[]
}

type AlertMstItemWithId = AlertMstItem & RecordEditEx

function MasterItemAlertDialog({
  open,
  onClose,
  onApplyEdits,
  onNavigateToRecord,
  resetSignal,
  searchParam,
  // center,
  items,
}: ItemAlertDialogProps) {
  const { loginUserInfo } = useAuthContext()
  const isReadOnly = loginUserInfo?.authType === AuthType.ReadOnly
  const dialogGridSearch = useGridSearch({ autoShowOnCtrlF: open })
  const { fetchAlertItems, isAlertItemsLoading } = useItem()
  const [rows, setRows] = useState<AlertMstItemWithId[]>([])
  const [pendingEdits, setPendingEdits] = useState<Record<string, ItemAlertDialogEdit>>({})
  const [hasLoaded, setHasLoaded] = useState(false)
  const [, setHasGridEdited] = useState(false)
  const [, setResultMessage] = useState<ResponseResult | undefined>(undefined)

  const { onGridReady, dateFormatter, numberValueFormatter, localeText, defaultColDef, createCellClassRules, selectValueFormatter } = useGridEdit({
    gridRef: dialogGridSearch.gridRef as React.RefObject<AgGridReact>,
    isLoading: isAlertItemsLoading,
    isUpdating: false,
    onUploadData: undefined,
    updateTrigger: undefined,
    setHasGridEdited,
    setResultMessage,
    columnMapping: {},
    primayKey: ['orgItemCode', 'itemCode'],
    createBlankRow: () => ({ ...initRecordEditEx(true) }),
    fieldValidate: () => false,
  })

  useEffect(() => {
    setRows([])
    setPendingEdits({})
    setHasLoaded(false)
  }, [resetSignal])

  const applyRowsForAction = useCallback(
    (
      selectedNodes: IRowNode[],
      actionLabel: string,
      updater: (row: AlertMstItemWithId) => AlertMstItemWithId,
      editBuilder: (row: AlertMstItemWithId) => ItemAlertDialogEdit
    ) => {
      if (selectedNodes.length === 0) return
      const selectedRowIds = new Set(selectedNodes.map((node) => node.data?.id))
      setRows((prev) =>
        prev.map((row) => {
          if (!selectedRowIds.has(row.id)) return row
          const nextEditedFields = new Set(row.editedFields)
          nextEditedFields.add(`action:${actionLabel}`)
          return {
            ...updater(row),
            isEdited: true,
            editedFields: nextEditedFields,
          }
        })
      )

      setPendingEdits((prev) => {
        const next = { ...prev }
        selectedNodes.forEach((node) => {
          const row = node.data as AlertMstItemWithId
          const key = row.orgItemCode ?? ''
          if (!key) return
          next[key] = { ...(next[key] ?? { orgItemCode: key }), ...editBuilder(row) }
        })
        return next
      })
    },
    []
  )

  useEffect(() => {
    const load = async () => {
      if (!open || hasLoaded) return

      const response = await fetchAlertItems({
        enabled: false,
        companyId: searchParam.companyId,
        orgSupplierId: searchParam.orgSupplierId || undefined,
      })
      if (!response) return

      setRows(
        response.data.list.map((item) => ({
          ...item,
          ...initRecordEditEx(),
        }))
      )
      setHasLoaded(true)
      // API で行を差し替えたときだけフィルター・検索をリセット（再オープンのみでは残す）
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const gridApi = dialogGridSearch.gridRef.current?.api
          if (gridApi && !gridApi.isDestroyed()) {
            gridApi.setFilterModel(null)
          }
          dialogGridSearch.clearSearch()
        })
      })
    }
    load()
  }, [open, hasLoaded, searchParam.companyId, searchParam.orgSupplierId])

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (MenuItemDef | DefaultMenuItem)[] => {
      const targets = collectRowNodesFromGridSelection(params.api, params.node)
      if (targets.length === 0) return []

      const canMasterRegister = targets.every((node) => node.data?.masterNotFound === true)
      const canShow =
        targets.length === 1 &&
        targets[0].data?.masterNotFound === false &&
        !!targets[0].data?.orgItemCode

      return [
        ...getClipboardContextMenuItems(params),
        'separator',
        {
          name: 'マスタ追加',
          disabled: !canMasterRegister || isReadOnly,
          action: () => {
            if (isReadOnly) return
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            applyRowsForAction(nodes, 'マスタ追加', (row) => row, (row) => ({
              orgItemCode: row.orgItemCode ?? '',
              masterRegister: true,
            }))
          },
        },
        {
          name: '取扱終了',
          disabled: isReadOnly,
          action: () => {
            if (isReadOnly) return
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            applyRowsForAction(
              nodes,
              '取扱終了',
              (row) => ({ ...row, isDiscontinued: true }),
              (row) => ({ orgItemCode: row.orgItemCode ?? '', isDiscontinued: true })
            )
          },
        },
        {
          name: '通知不要',
          disabled: isReadOnly,
          action: () => {
            if (isReadOnly) return
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            applyRowsForAction(
              nodes,
              '通知不要',
              (row) => ({ ...row, isMuted: true }),
              (row) => ({ orgItemCode: row.orgItemCode ?? '', isMuted: true })
            )
          },
        },
        'separator',
        {
          name: '表示',
          disabled: !canShow,
          action: () => {
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            if (nodes.length !== 1) return
            const row = nodes[0].data as AlertMstItemWithId
            if (!row || row.masterNotFound) return
            onNavigateToRecord({ orgItemCode: row.orgItemCode ?? '' })
            onClose()
          },
        },
      ]
    },
    [applyRowsForAction, isReadOnly, onClose, onNavigateToRecord]
  )

  const handleOk = useCallback(() => {
    if (isReadOnly) {
      onClose()
      return
    }
    onApplyEdits(Object.values(pendingEdits))
    onClose()
  }, [isReadOnly, onApplyEdits, onClose, pendingEdits])

  const columns = useMemo<ColDef[]>(
    () => [
      {
        headerName: '!',
        field: 'editInfo',
        width: 70,
        pinned: 'left',
        cellRenderer: EditStatusCellRenderer,
        valueGetter: (params: any) => {
          const edited = Array.from((params.data?.editedFields ?? new Set<string>()) as Set<string>)
            .filter((v) => v.startsWith('action:'))
            .map((v) => v.replace('action:', ''))
          return edited.join(',\n')
        },
        tooltipValueGetter: (params: any) => {
          const edited = Array.from((params.data?.editedFields ?? new Set<string>()) as Set<string>)
            .filter((v) => v.startsWith('action:'))
            .map((v) => v.replace('action:', ''))
          return edited.join(',\n')
        },
      },
      {
        headerName: 'アラート種別',
        field: 'alertTypeName',
        flex: 1,
      },
      // {
      //   headerName: '自社センター',
      //   field: 'orgCenterId',
      //   width: 200,
      //   valueFormatter: (params: any) => selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
      // },
      {
        headerName: '社内商品コード',
        field: 'orgItemCode',
        flex: 1,
        // valueFormatter: (params: any) => {
        //   const formatted = selectValueFormatter(params.value, items, 'orgItemCode', 'itemName')
        //   if (formatted) return formatted
        //   const code = params.value ?? ''
        //   const name = params.data?.mstItemItemName ?? ''
        //   return code && name ? `[${code}]${name}` : code
        // },
      },
      { headerName: '商品名', field: 'mstItemItemName', width: 200 },
      {
        headerName: 'マスタ未登録',
        children: [
          {
            headerName: 'マスタ未登録',
            field: 'masterNotFound',
            cellClassRules: createCellClassRules('masterNotFound', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            flex: 1 
          },
          // { headerName: '最小実績日', field: 'minRecDate', valueFormatter: dateFormatter, width: 200 },
        ]
      },
      {
        headerName: '休止アラート',
        children: [
          // { headerName: 'リニューアル確認',
          //   field: 'unmatchRenewal',
          //   cellClassRules: createCellClassRules('unmatchRenewal', true, 'alert_middle'),
          //   cellRenderer: 'agCheckboxCellRenderer',
          //   cellClass: 'boolean-cell',
          //   width: 200 },
          // { headerName: '後継社内商品コード', field: 'replaceOrgItemCode', width: 200 },
          // { headerName: 'JANコード（ケース）', field: 'janCase', width: 200 },
          // { headerName: '最新商品フラグ', field: 'recencyFlg', cellRenderer: 'agCheckboxCellRenderer',
          //   cellClass: 'boolean-cell',
          //   width: 200 },
          // { headerName: '適用開始日', field: 'trStartDate', valueFormatter: dateFormatter, width: 200 },
          {
            headerName: '休止アラート',
            field: 'isInactive',
            cellClassRules: createCellClassRules('isInactive', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            flex: 1 },
          {
            headerName: '最終出荷日',
            field: 'lastShipDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            flex: 1,
          },
          {
            headerName: '最小入荷実績日',
            field: 'minArrivalDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            flex: 1,
          },
          {
            headerName: '最終入荷実績日',
            field: 'lastArrivalDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            flex: 1,
          },
        ]
      }
    ],
    [dateFormatter, items, numberValueFormatter, selectValueFormatter]
  )

  return (
    <Modal keepMounted open={open} onClose={onClose}>
      <DialogWrapper>
        <Box sx={{ pt: 2 }}>
          <Typography component="div" variant="largeBold">
            商品マスタアラート
          </Typography>

          <SearchBox
            gridRef={dialogGridSearch.searchBoxProps.gridRef}
            findSearchValue={dialogGridSearch.searchBoxProps.findSearchValue}
            setFindSearchValue={dialogGridSearch.searchBoxProps.setFindSearchValue}
            activeMatchNum={dialogGridSearch.searchBoxProps.activeMatchNum}
            onFindChanged={dialogGridSearch.searchBoxProps.onFindChanged}
            isVisible={dialogGridSearch.searchBoxProps.isVisible}
            onHide={dialogGridSearch.searchBoxProps.onHide}
          />

          <div style={{ height: '70vh', width: '100%' }} className="ag-theme-balham">
            <AgGridReact
              ref={dialogGridSearch.gridRef}
              rowData={rows}
              getRowId={(params) => params.data.id || ''}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              localeText={localeText}
              onGridReady={onGridReady}
              loadingOverlayComponent={LoadingOverlay}
              loading={isAlertItemsLoading}
              findSearchValue={dialogGridSearch.gridSearchProps.findSearchValue}
              onFindChanged={dialogGridSearch.gridSearchProps.onFindChanged}
              getContextMenuItems={getContextMenuItems}
              cellSelection={{
                enableHeaderHighlight: true,
                handle: { mode: 'fill', direction: 'xy' },
              }}
              suppressRowClickSelection={false}
            />
          </div>
          <Grid direction="row" container justifyContent="end" sx={{ mt: 2 }}>
            <Grid component="div">
              <Button variant="contained" color="secondary" onClick={handleOk}>
                OK
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogWrapper>
    </Modal>
  )
}

export default MasterItemAlertDialog
